import React from 'react';
import { QuestionAnswerPayload, QuestionData } from '../services/aiAssistant';
import { message } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mdStyles from './markdown.less';
import QuestionCard from './QuestionCard';
import ThinkingCard, { ThinkingToolCall } from './ThinkingCard';
import TokenTag, { MessageTokens } from './TokenTag';

export interface MessageThinking {
  text: string;
  tools: ThinkingToolCall[];
  status: 'in-progress' | 'done' | 'failed';
  expanded: boolean;
}

interface Props {
  role: 'user' | 'ai';
  content: string;
  createTime?: string;
  thinking?: MessageThinking;
  onToggleThinking?: () => void;
  tokens?: MessageTokens;
  /** 分享多选态：是否展示选择圆圈 */
  selectable?: boolean;
  /** 当前消息所在 turn 是否被选中 */
  selected?: boolean;
  /** 失败 turn 等不可勾选场景：禁用 + 不渲染 */
  selectDisabled?: boolean;
  /** 点击选择圆圈 */
  onToggleSelect?: () => void;
  /** 点击复制（仅 AI 气泡可见） */
  onShare?: () => void;
  /** askQuestion：Agent 抛出的问题 */
  questionData?: QuestionData;
  /** askQuestion：作答只读快照 */
  questionAnswers?: QuestionAnswerPayload;
  /** askQuestion：问题卡片是否禁用交互（非末尾/历史） */
  questionDisabled?: boolean;
  /** askQuestion：提交作答 */
  onSubmitAnswer?: (payload: QuestionAnswerPayload) => void;
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}:${pad2(d.getSeconds())}`;
}

const timeStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#8a94a6',
  lineHeight: 1.4
};

async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 走 fallback
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const actionBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: '#8a94a6',
  cursor: 'pointer',
  transition: 'all 0.15s',
  padding: 0
};

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" width={13} height={13}>
      <rect x="4" y="4" width="9" height="10" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3 11V3a1 1 0 011-1h7" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" width={13} height={13}>
      <circle cx="4" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="12" cy="3.5" r="1.8" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="12" cy="12.5" r="1.8" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5.5 7l5-2.7M5.5 9l5 2.7" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function SelectCircle({
  selected,
  disabled,
  onClick
}: {
  selected: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `1.5px solid ${selected ? '#1677ff' : disabled ? '#d9d9d9' : '#bfbfbf'}`,
        background: selected ? '#1677ff' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        padding: 0,
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s'
      }}
    >
      {selected && (
        <svg viewBox="0 0 12 12" fill="none" width={10} height={10}>
          <path d="M3 6.2l2 2 4-4.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

export default function Message({
  role,
  content,
  createTime,
  thinking,
  onToggleThinking,
  tokens,
  selectable,
  selected,
  selectDisabled,
  onToggleSelect,
  onShare,
  questionData,
  questionAnswers,
  questionDisabled,
  onSubmitAnswer
}: Props) {
  const timeText = formatTime(createTime);
  const showActions = role === 'ai' && !selectable && !!content && thinking?.status !== 'in-progress';

  const handleCopy = async () => {
    const ok = await copyText(content);
    if (ok) {
      message.success('已复制');
    } else {
      // 例外：非接口调用、纯前端业务文案，规约允许
      message.error('复制失败');
    }
  };

  if (role === 'user') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: 14,
          gap: 8,
          justifyContent: 'flex-end'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 4,
            minWidth: 0
          }}
        >
          <div
            style={{
              maxWidth: '100%',
              padding: '10px 14px',
              background: 'linear-gradient(135deg, #2A6FF5, #5B8DEF)',
              color: '#fff',
              borderRadius: '14px 14px 4px 14px',
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {content}
          </div>
          {timeText && <div style={timeStyle}>{timeText}</div>}
        </div>
        {selectable && (
          <div style={{ marginTop: 8 }}>
            <SelectCircle selected={!!selected} disabled={selectDisabled} onClick={onToggleSelect} />
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'flex-start' }}>
      {selectable ? (
        <div style={{ marginTop: 6 }}>
          <SelectCircle selected={!!selected} disabled={selectDisabled} onClick={onToggleSelect} />
        </div>
      ) : (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2A6FF5, #5B8DEF)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" width={15} height={15}>
            <path d="M14 2L5 13.5h6.5L10 22l9.5-11H13L14 2Z" fill="#fff" />
          </svg>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {thinking && (
          <ThinkingCard
            text={thinking.text}
            tools={thinking.tools}
            status={thinking.status}
            expanded={thinking.expanded}
            onToggle={onToggleThinking || (() => {})}
          />
        )}
        {content && (
          <div
            style={{
              maxWidth: '100%',
              padding: '10px 14px',
              background: '#fff',
              color: '#262626',
              borderRadius: '14px 14px 14px 4px',
              wordBreak: 'break-word',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #F0F0F0'
            }}
          >
            <div className={mdStyles.aiBubbleContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          </div>
        )}
        {questionData && onSubmitAnswer && (
          <QuestionCard
            questionData={questionData}
            questionAnswers={questionAnswers}
            disabled={questionDisabled}
            onSubmit={onSubmitAnswer}
          />
        )}
        {timeText && content && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={timeStyle}>{timeText}</span>
            {tokens?.total !== undefined && tokens.total > 0 && thinking?.status !== 'in-progress' && (
              <TokenTag
                variant="message"
                total={tokens.total}
                input={tokens.input}
                output={tokens.complete}
                cache={tokens.cache}
              />
            )}
            {showActions && (
              <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <button
                  type="button"
                  title="复制"
                  onClick={handleCopy}
                  style={actionBtnStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f2f3f5';
                    e.currentTarget.style.color = '#4e5969';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#8a94a6';
                  }}
                >
                  <CopyIcon />
                </button>
                {onShare && (
                  <button
                    type="button"
                    title="分享"
                    onClick={onShare}
                    style={actionBtnStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f2f3f5';
                      e.currentTarget.style.color = '#4e5969';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#8a94a6';
                    }}
                  >
                    <ShareIcon />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
